import { Outlet, useNavigate } from "react-router";
import { useEffect } from "react";
import { Sidebar } from "./Sidebar";
import { useUser } from "@/lib/hooks";
import { useStore } from "@/lib/store";
import { authApi, isMockMode } from "@/lib/api";

export function DashboardLayout() {
  const { data: user, isLoading, error } = useUser();
  const setUser = useStore((s) => s.setUser);
  const toasts = useStore((s) => s.toasts);
  const dismissToast = useStore((s) => s.dismissToast);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      setUser(user);
    }
  }, [user, setUser]);

  useEffect(() => {
    async function sendTokenToExtension() {
      if (
        !user ||
        !import.meta.env.VITE_EXTENSION_ID ||
        typeof window === "undefined"
      ) {
        return;
      }

      const chromeApi = (window as any).chrome;
      if (!chromeApi?.runtime?.sendMessage) return;

      try {
        const { token } = await authApi.token();
        chromeApi.runtime.sendMessage(
          import.meta.env.VITE_EXTENSION_ID,
          { type: "SET_TOKEN", token },
          () => {
            // ignore response; extension may not be installed
          }
        );
      } catch {
        // Silent fail (extension not installed or token endpoint unavailable)
      }
    }

    sendTokenToExtension();
  }, [user]);

  useEffect(() => {
    if (error) {
      navigate("/login");
    }
  }, [error, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-black border-t-transparent" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="flex h-screen bg-white">
      {/* Sidebar — hidden on mobile */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <MobileNav />

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="fixed right-4 top-4 z-[120] flex w-[320px] flex-col gap-2">
          {toasts.map((toast) => {
            const styleMap: Record<string, string> = {
              success: "border-green-200 bg-green-50 text-green-900",
              error: "border-danger/20 bg-red-50 text-danger",
              info: "border-border bg-surface text-neutral-700",
            };
            return (
              <div
                key={toast.id}
                className={`rounded-lg border px-4 py-3 text-sm shadow-sm ${
                  styleMap[toast.type]
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    {toast.title && (
                      <p className="font-semibold leading-5">{toast.title}</p>
                    )}
                    <p className="leading-5 text-current/80">{toast.message}</p>
                  </div>
                  <button
                    onClick={() => dismissToast(toast.id)}
                    className="text-xs text-current/60 hover:text-current"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mock mode indicator */}
      {isMockMode && (
        <div className="fixed top-2 right-2 z-[100] rounded-full bg-black px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-white opacity-60">
          Mock Data
        </div>
      )}
    </div>
  );
}

function MobileNav() {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 flex border-t border-border bg-white lg:hidden">
      <MobileNavLink to="/dashboard" label="Overview" />
      <MobileNavLink to="/dashboard/posts" label="Posts" />
      <MobileNavLink to="/dashboard/heatmap" label="Heatmap" />
      <MobileNavLink to="/dashboard/billing" label="Billing" />
      <MobileNavLink to="/dashboard/settings" label="Settings" />
    </nav>
  );
}

function MobileNavLink({ to, label }: { to: string; label: string }) {
  // Use simple anchor to avoid NavLink complexity in mobile
  const isActive = window.location.pathname === to;
  // Preserve mock query param when navigating
  const href = isMockMode ? `${to}?mock=true` : to;
  return (
    <a
      href={href}
      className={`flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] ${
        isActive ? "font-semibold text-black" : "text-neutral-400"
      }`}
    >
      {label}
    </a>
  );
}
